/**
 * TCX-866 — validate a royalty / advertising-fee percentage on submit.
 *
 * The migration preserves a real non-numeric legacy value (e.g. `'$475/month'`,
 * `'Ramps over four years...'`) verbatim under `type: 'percent'`. The form shows
 * that value, so a strict numeric rule would reject it and block the franchisor
 * from saving ANY other field on the form without first overwriting the
 * preserved content. So an UNCHANGED value — equal to what was hydrated from the
 * profile — always passes; only a NEW edit must be a valid 0–100 percentage,
 * matching the backend's numeric rule for a new `percent` write.
 *
 * Returns an error message, or `null` when the value is acceptable.
 */
export function validatePercentageValue(
  value: unknown,
  originalValue: unknown,
): string | null {
  if (!value) return null;

  // A preserved legacy value the franchisor did not touch — let it save.
  if (originalValue != null && String(value) === String(originalValue)) {
    return null;
  }

  const raw = String(value);
  // Shape, AND at least one digit (so '.' / '' fall through), AND finite (so a
  // parseFloat NaN can't survive the range check below — NaN < 0 is false).
  if (!/^\d*\.?\d*$/.test(raw) || !/\d/.test(raw)) {
    return 'Please enter a valid percentage';
  }

  const numValue = parseFloat(raw);
  if (!Number.isFinite(numValue) || numValue < 0 || numValue > 100) {
    return 'Percentage must be between 0 and 100';
  }

  return null;
}
